import React, { useEffect, useState, Fragment } from 'react';
import { useSelector } from 'react-redux';

import { TIME_FILTER } from '@/constants/timeConstants';
import NewReviewCard from '@/components/elements/Card/ReviewCard';
import { Button, Fab } from '@/components/elements/forms/buttons';
import Popover from '@/components/elements/Popover';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import { LegacyModalHeader } from '@/components/modules/mobile';
import { ModalContent, ModalDialog } from '@/components/modules/modals';
import { ReviewStats } from '@/components/modules/review';
import { getAuthorInitials } from '@/features/ProfilePages/helpers/review';
import { isServer, server } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { ReviewsState } from '@/reducers/reviewsReducer';
import { Place } from '@/types/state/shared';
import { Review } from '@/types/reviews';
import Card from '@/components/elements/Card/Card';

const RESULT_PER_PAGE = 3;
const RESULT_PER_PAGE_LOAD_MORE_IN_MODAL = 30;

export function LegacyReviewsHeader() {
  const supportsPopover = !isServer && HTMLElement.prototype.hasOwnProperty('popover');
  const [popoverVisible, setPopoverVisible] = useState(false);
  const togglePopover = () => {
    setPopoverVisible(!popoverVisible);
  };
  return (
    <Fragment>
      <div className="mb-xs flex items-center">
        <h2 className="text-h-m flex-1 font-bold">{_s('reviewsTitle')}</h2>

        <button
          id="reviews-info"
          {...{ popovertarget: 'reviews-info-popover' }}
          className="trigger-reviews-info-popover focus-visible:outline-black-900 rounded-sm focus:outline-none focus-visible:outline"
          onClick={supportsPopover ? undefined : togglePopover}>
          <Icon variant="info-circle" color="fg_link" style={{ width: '20px', height: '20px' }} />
        </button>
      </div>

      {(supportsPopover || (!supportsPopover && popoverVisible)) && (
        <Popover id="reviews-info-popover" className={`${!supportsPopover ? 'z-[100]' : ''}`}>
          <div className="space-y-md bg-bg_inverse p-lg w-full max-w-[400px] rounded-lg">
            <div className="flex items-start justify-between">
              <span className="text-fg_alwaysLight max-w-[200px] text-lg font-bold">
                {_s('leaveReviewPopover.title')}
              </span>
            </div>
            <p className="text-fg_secondary_inverse" id="popover-content">
              {_s('leaveReviewPopover.body')}
            </p>
          </div>
        </Popover>
      )}
    </Fragment>
  );
}

type ReviewsProps = {
  place: Place;
  reviewsUid: string;
  hideStaff?: boolean;
  isMpReviews?: boolean;
  tooltipTargetRef?: React.RefObject<HTMLDivElement>;
};

function LegacyReviews(props: ReviewsProps) {
  const { place, reviewsUid, hideStaff = false, isMpReviews, tooltipTargetRef } = props;

  // Stats/counts come from the same redux-backed source (populated via /reviews/v2/details)
  // that the new reviews stack and PlacePageHeader's rating badge already read — this view is
  // legacy, the data underneath it isn't.
  const placeReviews = useSelector((state: { reviews: ReviewsState }) => state.reviews?.byUid?.[reviewsUid]?.details);
  const { stats, ratingCounts, reviewCount = 0, reviewCounts } = placeReviews || {};
  const { about: { settings: { subscriptionType } } = {} } = place;
  const [showModal, setShowModal] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [modalReviews, setModalReviews] = useState<Review[]>([]);
  const [nextReviewPageData, setNextReviewPageData] = useState({ page: 1, limit: RESULT_PER_PAGE });
  const [nextModalReviewPageData, setModalNextReviewPageData] = useState<{ page?: number; limit?: number }>({
    page: 1,
    limit: RESULT_PER_PAGE_LOAD_MORE_IN_MODAL,
  });
  const [haveMoreReviews, setHaveMoreReviews] = useState(true);
  const [haveMoreModalReviews, setHaveMoreModalReviews] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [ratingFilter, setRatingFilter] = useState(0);
  const { isMobileView } = useMobileView();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchModalReviews();
  }, [ratingFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchReviews();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function fetchReviewsByRating(rating = 0) {
    setRatingFilter(rating);
    if (rating !== ratingFilter) {
      setModalReviews([]);
      setModalNextReviewPageData({ page: 1, limit: RESULT_PER_PAGE_LOAD_MORE_IN_MODAL });
      setHaveMoreModalReviews(true);
    }
    setShowModal(true);
  }

  async function fetchModalReviews() {
    if (!haveMoreModalReviews) {
      return;
    }
    setIsLoading(true);
    const result = await server.request
      .get('/reviews/v2/list', {
        params: {
          placeId: place.id,
          page: nextModalReviewPageData.page,
          limit: nextModalReviewPageData.limit,
          isMpReviews,
          hasMessage: true,
          rating: ratingFilter,
          timeFilter: TIME_FILTER.FOREVER,
        },
      })
      .then(server.handleSuccess)
      .catch(server.handleError);
    setModalReviews(modalReviews.concat(result.items));
    setModalNextReviewPageData(result.nextPage || {});
    setHaveMoreModalReviews(
      result.nextPage && result.nextPage && result.nextPage.limit && result.nextPage.limit > 0 ? true : false,
    );
    setIsLoading(false);
  }

  async function fetchReviews(page?: number) {
    if (!haveMoreReviews && !page) {
      return;
    }
    setIsLoading(true);
    const result = await server.request
      .get('/reviews/v2/list', {
        params: {
          placeId: place.id,
          page: page ?? nextReviewPageData.page,
          limit: nextReviewPageData.limit ?? RESULT_PER_PAGE,
          isMpReviews,
          hasMessage: true,
          rating: 0,
          timeFilter: TIME_FILTER.FOREVER,
        },
      })
      .then(server.handleSuccess)
      .catch(server.handleError);

    setReviews(result.items);
    setTotalPages(Math.ceil(result.count / RESULT_PER_PAGE));
    setNextReviewPageData(result.nextPage || {});
    setHaveMoreReviews(
      result.nextPage && result.nextPage && result.nextPage.limit && result.nextPage.limit > 0 ? true : false,
    );
    setIsLoading(false);
  }

  const handleClickNextPage = () => {
    fetchReviews();
    setPage(nextReviewPageData.page);
  };

  const handleClickPreviousPage = () => {
    fetchReviews(page - 1);
    setPage((prev) => prev - 1);
  };

  return (
    <Card size={isMobileView ? 'lg' : 'xl'}>
      <div id="reviews" className="gap-sm relative flex scroll-mt-[120px] flex-col">
        <LegacyReviewsHeader />
        <div className="gap-2xl flex flex-col">
          <ReviewStats
            className="!py-0"
            stats={stats}
            ratingCounts={ratingCounts}
            reviewCounts={reviewCounts}
            onRatingClick={fetchReviewsByRating}
          />
          {reviews.length > 0 && (
            <div className="gap-md flex flex-col">
              {reviews.map((review, key) => {
                const initials = getAuthorInitials(review.author.name);
                return (
                  <NewReviewCard
                    tooltipTargetRef={tooltipTargetRef}
                    showVerified={false}
                    verified={true}
                    key={key}
                    placeId={place.id.toString()}
                    reviewId={review.id}
                    companyReply={review.reply ? { ...review.reply, author: review.reply.author.name } : undefined}
                    review={{
                      ...review.review,
                      initials,
                      author: review.author.name,
                      createdAt: review.createdAt,
                      employee: review.employee?.name ? review.employee.name : undefined,
                    }}
                  />
                );
              })}
            </div>
          )}
          {reviewCount > RESULT_PER_PAGE && (
            <div className="flex justify-between">
              <Fab
                icon={<Icon variant="chevron-left" color={page === 1 ? 'fg_disabled' : 'fg_primary'} />}
                onClick={handleClickPreviousPage}
                size="sm"
                disabled={page === 1}
                variant="secondary"
              />
              <div className="text-fg_secondary">
                <span>{_s(`features.ProfilePages.components.Reviews.pagination.page`)}</span> <span>{page}</span>{' '}
                <span>{_s(`features.ProfilePages.components.Reviews.pagination.of`)}</span>{' '}
                <span>{totalPages === 0 ? 1 : totalPages}</span>
              </div>
              <Fab
                icon={
                  <Icon
                    variant="chevron-right"
                    color={page === totalPages || totalPages === 0 ? 'fg_disabled' : 'fg_primary'}
                  />
                }
                onClick={handleClickNextPage}
                disabled={page === totalPages || totalPages === 0}
                size="sm"
                variant="secondary"
              />
            </div>
          )}
          {!isServer && (
            <ModalDialog
              isOpen={showModal}
              appElement={document.getElementById('root')}
              onRequestClose={() => setShowModal(false)}>
              <ModalContent size="lg">
                <div className="sm:hidden">
                  <LegacyModalHeader
                    action={() => setShowModal(false)}
                    cancel
                    top={
                      <span className="pl-xs font-semibold">
                        {place.about && place.about.name ? place.about.name : ''}
                      </span>
                    }
                  />
                </div>
                <div className="hidden w-full justify-end sm:flex">
                  <span className="mr-sm mt-sm">
                    <Fab icon={<Icon variant="close" />} onClick={() => setShowModal(false)} size="md" variant="link" />
                  </span>
                </div>
                <div className="px-md py-2xl sm:py-0">
                  <h2 className="text-lg font-semibold">{_s('reviewsTitle')}</h2>
                  <ReviewStats
                    stats={stats}
                    ratingCounts={ratingCounts}
                    reviewCounts={reviewCounts}
                    onRatingClick={fetchReviewsByRating}
                    ratingFilter={ratingFilter}
                  />
                  <div className="mb-xl gap-md flex flex-row flex-wrap items-stretch justify-between">
                    {modalReviews.map((review, key) => {
                      const initials = getAuthorInitials(review.author.name);

                      return (
                        <NewReviewCard
                          tooltipTargetRef={tooltipTargetRef}
                          showVerified={false}
                          verified={true}
                          key={key}
                          placeId={place.id.toString()}
                          reviewId={review.id}
                          companyReply={
                            review.reply && subscriptionType !== 'essential' && subscriptionType !== 'mini'
                              ? { ...review.reply, author: review.reply.author.name }
                              : undefined
                          }
                          review={{
                            ...review.review,
                            initials,
                            author: review.author.name,
                            createdAt: review.createdAt,
                            employee: review.employee?.name && !hideStaff ? review.employee.name : undefined,
                          }}
                        />
                      );
                    })}
                    {isLoading && (
                      <div className="flex w-full justify-center">
                        <LoadingIcon className="h-2xl w-2xl" />
                      </div>
                    )}
                    {modalReviews.length > 0 && haveMoreModalReviews && (
                      <div className="mx-auto">
                        <Button variant="primary" size="sm" onClick={fetchModalReviews}>
                          {_s('showMore')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </ModalContent>
            </ModalDialog>
          )}
        </div>
      </div>
    </Card>
  );
}

export default LegacyReviews;
